import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { get, post, restoreSession, setAccessToken } from '../api/client.ts'
import { CACHE_KEY } from '../lib/constants.ts'
import type { User } from '../api/types.ts'

interface AuthValue {
  user: User | null
  loading: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const queryClient = useQueryClient()

  useEffect(() => {
    // La sesion se recupera de la cookie httpOnly, no de localStorage.
    void (async () => {
      if (await restoreSession()) {
        try {
          setUser(await get<User>('/api/me'))
        } catch {
          setAccessToken(null)
        }
      }
      setLoading(false)
    })()
  }, [])

  const login = useCallback(async (email: string, password: string) => {
    const data = await post<{ accessToken: string; user: User }>('/api/auth/login', {
      email: email.trim().toLowerCase(),
      password,
    })
    setAccessToken(data.accessToken)
    setUser(data.user)
  }, [])

  const logout = useCallback(async () => {
    try {
      await post('/api/auth/logout')
    } finally {
      setAccessToken(null)
      setUser(null)
      // La caché guardada contiene saldos y tareas. En un móvil compartido no
      // puede sobrevivir al cierre de sesión.
      queryClient.clear()
      try {
        window.localStorage.removeItem(CACHE_KEY)
      } catch {
        /* si el almacenamiento está bloqueado, no hay nada que borrar */
      }
    }
  }, [queryClient])

  return <AuthContext value={{ user, loading, login, logout }}>{children}</AuthContext>
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext)
  if (!value) throw new Error('useAuth debe usarse dentro de AuthProvider')
  return value
}
